import { SemiBold14, SemiBold20 } from '@/components/design-system';
import styles from './policyDetails.module.scss';
import { useRouter } from 'next/navigation';
import { ROUTER_GROUPS } from '@/core/constants/routes';
import PolicyDetailsCard from '@/app/(portal)/(policy)/components/policy-details/policyDetailsCard';
import { usePolicyDetails } from './policyDetailsController';
import { useEffect, useMemo } from 'react';
import { PolicyObject } from '@/data/api/policy/types/policyObject.types';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { getAllPoliciesUtil } from '@/core/utils/policy.util';
import { useGeneralContext } from '@/contexts/general/general.context';
import ExpiredCancelledComp from './expiredCancelled/expiredCancelledComp';
import NoPolicyLink from './linkPolicy/noPolicyLink';
import { useGtm } from '@/contexts/analytics/analytics.context';
import { useSuperValue } from '@/hooks/useSuperValue';
import { getPolicyNumberWithParamCombined } from '@/core/utils/misc.utils';
import { NA_PLACEHOLDER } from '@/core/constants/misc';

export default function PolicyDetails() {
  const router = useRouter();
  const { getPolicyDetails, actionClickHandler } = usePolicyDetails();
  const [policyDetails, setPolicyDetails] = useSuperValue<
    Record<string, PolicyObject[]>
  >({}, true);
  const { dashboardLabels, downloadPolicyStatus } = useDashboard();
  const { policyDetailsCms } = dashboardLabels;
  const { policies } = useGeneralContext();
  const gtmModule = useGtm();

  useEffect(() => {
    if (!policies.loading) {
      const data = getPolicyDetails();
      setPolicyDetails.setValue(data);
      setPolicyDetails.setLoading(false);
    }
  }, [getPolicyDetails, policies.loading]);

  const activeAndUpcomingPolicies = useMemo(() => {
    return Object.entries(policyDetails.value).filter(([key]) =>
      ['Active', 'Upcoming'].includes(key)
    );
  }, [policyDetails.value]);

  const expiredAndCancelledPolicies = useMemo(() => {
    return Object.entries(policyDetails.value).filter(([key]) =>
      ['Expired', 'Cancelled'].includes(key)
    );
  }, [policyDetails.value]);

  if (policyDetails.loading) {
    return null;
  }

  return (
    <div className={styles['main-con']}>
      <SemiBold20>
        {Object.values(policyDetails.value).length <= 1
          ? policyDetailsCms.secondtitle
          : policyDetailsCms.title}
      </SemiBold20>
      <div className={styles['insurance-con']}>
        {getAllPoliciesUtil(policies.value).length === 0 && <NoPolicyLink />}

        {Object.values(policyDetails.value).length > 0 && (
          <div className={styles['poilicies-con']}>
            {activeAndUpcomingPolicies.map(([key, value]) => {
              return (
                <div key={key} className={styles['cards-con']}>
                  <SemiBold14>{`${key.toUpperCase()} POLICIES`}</SemiBold14>
                  {value.map((policy, index) => {
                    return (
                      <PolicyDetailsCard
                        key={index}
                        viewDetailsClick={async () => {
                          router.push(
                            ROUTER_GROUPS.policy.policyDetails(
                              await getPolicyNumberWithParamCombined(
                                policy.policyNo,
                                policy.multiYearType || NA_PLACEHOLDER
                              )
                            )
                          );
                          gtmModule.dashboard().sendSelectPromotion({
                            id: 'Policy details card',
                            name: 'View details',
                          });
                        }}
                        actionClick={(e) => actionClickHandler(e, policy)}
                        policy={policy}
                        cmsLabels={policyDetailsCms.policyCard}
                        actionGtmEvent={
                          gtmModule.dashboard().sendSelectPromotion
                        }
                        downloadPolicyStatus={downloadPolicyStatus}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
        {
          // TEMP: removed for interim
          //
          // {Object.values(policyDetails.value).length > 0 && <LinkPolicyComp />}
        }
        {expiredAndCancelledPolicies.map(([key, val]) => {
          return <ExpiredCancelledComp title={key} key={key} policies={val} />;
        })}
      </div>
    </div>
  );
}
